/**
 * The outboxes (wave b12 frontend item 4; wave COM items 4 and 5): the tenant's power
 * user reads every email and webhook call queued, newest first, and what became of it.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Each outbox is a DataTable over one server page (limit 50, the server's offset paging
 * beneath it). The status filter is the server's own (`status=`), and each choice carries
 * the server's count of that status over the whole outbox. A FAILED row says why in the
 * words the server recorded (`last_error`); a SUPPRESSED email says why it was never
 * sent; a DEAD webhook keeps its last answer. Anyone but the power user is answered
 * absence (404), rendered as the absence panel.
 */

import { useMemo, useState } from "react";

import { ApiError } from "../api/client";
import {
  EMAIL_STATUSES,
  WEBHOOK_STATUSES,
  listEmailOutbox,
  listWebhookOutbox,
  type EmailOutboxRow,
  type OutboxPage,
  type WebhookOutboxRow,
} from "../api/outbox";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { zonedStamp } from "../lib/zonedTime";
import { AbsencePanel, EmptyState, Pagination, SkeletonRows, StateChip } from "./parts";
import { useQuery, type QueryState } from "./useQuery";

const PAGE = 50;

type Tab = "email" | "webhook";

export function OutboxPage(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>("email");
  const copy = strings.outbox;
  return (
    <>
      <h2 className="q-shell__heading">{copy.heading}</h2>
      <p className="q-shell__lede">{copy.lede}</p>
      <div className="q-row q-gap-top" role="tablist" aria-label={copy.heading}>
        {(["email", "webhook"] as const).map((one) => (
          <button
            key={one}
            type="button"
            role="tab"
            aria-selected={tab === one}
            data-testid={`outbox-tab-${one}`}
            className={`q-button q-button--quiet${tab === one ? " q-mini--primary" : ""}`}
            onClick={() => {
              setTab(one);
            }}
          >
            {one === "email" ? copy.emailTab : copy.webhookTab}
          </button>
        ))}
      </div>
      {tab === "email" ? <EmailOutbox /> : <WebhookOutbox />}
    </>
  );
}

function isAbsence(state: QueryState<unknown>): boolean {
  return (
    state.kind === "failed" && state.error instanceof ApiError && state.error.kind === "absence"
  );
}

/** The status filter: All, then each status with the server's count of it. */
function StatusFilter({
  statuses,
  labels,
  counts,
  chosen,
  onChoose,
  testId,
}: {
  readonly statuses: readonly string[];
  readonly labels: Readonly<Record<string, string>>;
  readonly counts: Readonly<Record<string, number>>;
  readonly chosen: string | null;
  readonly onChoose: (status: string | null) => void;
  readonly testId: string;
}): React.JSX.Element {
  const copy = strings.outbox;
  const all = statuses.reduce((sum, one) => sum + (counts[one] ?? 0), 0);
  return (
    <div
      className="q-actions-row q-gap-top"
      role="group"
      aria-label={copy.statusFilterLabel}
      data-testid={testId}
    >
      {[null, ...statuses].map((one) => (
        <button
          key={one ?? "all"}
          type="button"
          aria-pressed={chosen === one}
          data-testid={`${testId}-${(one ?? "all").toLowerCase()}`}
          className={`q-button${chosen === one ? " q-mini--primary" : " q-button--quiet"}`}
          onClick={() => {
            onChoose(one);
          }}
        >
          {copy.statusCount(
            one === null ? copy.allStatuses : (labels[one] ?? one),
            one === null ? all : (counts[one] ?? 0),
          )}
        </button>
      ))}
    </div>
  );
}

/** The outbox body: its states, its table over one server page, the server's paging. */
function OutboxBody<T>({
  state,
  chosen,
  offset,
  onMove,
  table,
}: {
  readonly state: QueryState<OutboxPage<T>>;
  readonly chosen: string | null;
  readonly offset: number;
  readonly onMove: (offset: number) => void;
  readonly table: (rows: readonly T[]) => React.JSX.Element;
}): React.JSX.Element {
  const copy = strings.outbox;
  if (state.kind === "loading") {
    return <SkeletonRows rows={6} columns={6} />;
  }
  if (state.kind === "failed" || state.kind === "absence") {
    return (
      <div className="q-refusal" role="alert">
        <p className="q-refusal__heading">{strings.common.failed}</p>
      </div>
    );
  }
  if (state.data.items.length === 0) {
    return chosen === null ? (
      <EmptyState title={copy.emptyTitle} body={copy.emptyBody} />
    ) : (
      <EmptyState title={copy.emptyFilteredTitle} body={copy.emptyFilteredBody} />
    );
  }
  return (
    <div className="q-gap-top" data-testid="outbox-register">
      {table(state.data.items)}
      {state.data.total > PAGE && (
        <Pagination total={state.data.total} limit={PAGE} offset={offset} onMove={onMove} />
      )}
    </div>
  );
}

function statusChip(label: string, status: string, failed: boolean): React.JSX.Element {
  return (
    <span data-testid="outbox-status" data-status={status}>
      <StateChip
        state={failed ? "REJECTED" : status === "PENDING" ? "OPEN" : "CLOSED"}
        label={label}
      />
    </span>
  );
}

function EmailOutbox(): React.JSX.Element {
  const token = useToken();
  const [status, setStatus] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const state = useQuery(
    (signal) => listEmailOutbox(token, { status, limit: PAGE, offset, signal }),
    [token, status, offset],
  );
  const columns = useMemo(() => emailColumns(), []);
  if (isAbsence(state)) {
    return <AbsencePanel />;
  }
  return (
    <section data-testid="outbox-email">
      <StatusFilter
        statuses={EMAIL_STATUSES}
        labels={strings.outbox.emailStatus}
        counts={state.kind === "ready" ? state.data.counts : {}}
        chosen={status}
        onChoose={(next) => {
          setStatus(next);
          setOffset(0);
        }}
        testId="outbox-email-status"
      />
      <OutboxBody
        state={state}
        chosen={status}
        offset={offset}
        onMove={setOffset}
        table={(rows) => (
          <DataTable
            data={rows}
            columns={columns}
            caption={strings.outbox.emailCaption}
            testId="email-outbox"
            getRowId={(row) => row.id}
            getRowTestId={() => "email-outbox-row"}
            pageSize={PAGE}
            footer={false}
          />
        )}
      />
    </section>
  );
}

function WebhookOutbox(): React.JSX.Element {
  const token = useToken();
  const [status, setStatus] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const state = useQuery(
    (signal) => listWebhookOutbox(token, { status, limit: PAGE, offset, signal }),
    [token, status, offset],
  );
  const columns = useMemo(() => webhookColumns(), []);
  if (isAbsence(state)) {
    return <AbsencePanel />;
  }
  return (
    <section data-testid="outbox-webhook">
      <StatusFilter
        statuses={WEBHOOK_STATUSES}
        labels={strings.outbox.webhookStatus}
        counts={state.kind === "ready" ? state.data.counts : {}}
        chosen={status}
        onChoose={(next) => {
          setStatus(next);
          setOffset(0);
        }}
        testId="outbox-webhook-status"
      />
      <OutboxBody
        state={state}
        chosen={status}
        offset={offset}
        onMove={setOffset}
        table={(rows) => (
          <DataTable
            data={rows}
            columns={columns}
            caption={strings.outbox.webhookCaption}
            testId="webhook-outbox"
            getRowId={(row) => row.id}
            getRowTestId={() => "webhook-outbox-row"}
            pageSize={PAGE}
            footer={false}
          />
        )}
      />
    </section>
  );
}

/** Why an email was not sent, in words: the recorded error, or the suppression reason. */
function emailWhy(row: EmailOutboxRow): string {
  if (row.status === "FAILED") {
    return row.lastError ?? "";
  }
  if (row.status === "SUPPRESSED" && row.suppressedReason !== null) {
    return strings.outbox.suppression[row.suppressedReason] ?? row.suppressedReason;
  }
  return "";
}

function emailColumns(): RegisterColumn<EmailOutboxRow>[] {
  const copy = strings.outbox;
  const label = (status: string): string => copy.emailStatus[status] ?? status;
  return [
    {
      id: "created",
      label: copy.columnCreated,
      text: (row) => zonedStamp(row.createdAt),
      sortValue: (row) => row.createdAt,
      className: "whitespace-nowrap",
    },
    { id: "recipient", label: copy.columnRecipient, text: (row) => row.recipientDisplayName },
    {
      id: "message",
      label: copy.columnMessage,
      text: (row) => row.message,
      className: "min-w-[16rem]",
    },
    {
      id: "status",
      label: copy.columnStatus,
      text: (row) => label(row.status),
      filter: true,
      render: (row) => statusChip(label(row.status), row.status, row.status === "FAILED"),
    },
    {
      id: "attempts",
      label: copy.columnAttempts,
      text: (row) => String(row.attempts),
      sortValue: (row) => row.attempts,
    },
    {
      id: "when",
      label: copy.columnWhen,
      text: (row) =>
        row.sentAt !== null
          ? copy.sentOn(zonedStamp(row.sentAt))
          : row.status === "PENDING"
            ? copy.nextAttempt(zonedStamp(row.nextAttemptAt))
            : copy.noNextAttempt,
      sortValue: (row) => row.sentAt ?? row.nextAttemptAt,
    },
    {
      id: "why",
      label: copy.columnWhy,
      text: emailWhy,
      className: "min-w-[14rem]",
      render: (row) => <span data-testid="outbox-why">{emailWhy(row)}</span>,
    },
  ];
}

/** Why a call failed or was given up, in the server's recorded words. */
function webhookWhy(row: WebhookOutboxRow): string {
  if (row.status !== "FAILED" && row.status !== "DEAD") {
    return "";
  }
  const code = row.lastStatusCode === null ? "" : strings.outbox.statusCode(row.lastStatusCode);
  return [row.lastError ?? "", code].filter((one) => one !== "").join(" ");
}

function webhookColumns(): RegisterColumn<WebhookOutboxRow>[] {
  const copy = strings.outbox;
  const label = (status: string): string => copy.webhookStatus[status] ?? status;
  return [
    {
      id: "created",
      label: copy.columnCreated,
      text: (row) => zonedStamp(row.createdAt),
      sortValue: (row) => row.createdAt,
      className: "whitespace-nowrap",
    },
    {
      id: "endpoint",
      label: copy.columnEndpoint,
      text: (row) => row.endpointUrl,
      className: "font-mono break-all",
    },
    {
      id: "event",
      label: copy.columnEvent,
      text: (row) => strings.webhookEvents[row.eventKind] ?? row.eventKind,
      filter: true,
    },
    {
      id: "status",
      label: copy.columnStatus,
      text: (row) => label(row.status),
      filter: true,
      render: (row) =>
        statusChip(label(row.status), row.status, row.status === "FAILED" || row.status === "DEAD"),
    },
    {
      id: "attempts",
      label: copy.columnAttempts,
      text: (row) => String(row.attempts),
      sortValue: (row) => row.attempts,
    },
    {
      id: "when",
      label: copy.columnDeliveredWhen,
      text: (row) =>
        row.deliveredAt !== null
          ? copy.deliveredOn(zonedStamp(row.deliveredAt))
          : row.status === "PENDING"
            ? copy.nextAttempt(zonedStamp(row.nextAttemptAt))
            : copy.noNextAttempt,
      sortValue: (row) => row.deliveredAt ?? row.nextAttemptAt,
    },
    {
      id: "why",
      label: copy.columnWhy,
      text: webhookWhy,
      className: "min-w-[14rem]",
      render: (row) => <span data-testid="outbox-why">{webhookWhy(row)}</span>,
    },
  ];
}
